import type { Option } from '@/shared/ui';

/**
 * Каталог собеседников. Живёт в сущности, а не в онбординге: тьютора выбирают в
 * онбординге, но показывают его и в чате, и в настройках — принадлежать одному
 * из этих мест он не может.
 *
 * Идентификаторы обязаны совпадать с `catalog.ts` на бэкенде: там персона и
 * голос для синтеза, здесь — то, как он выглядит. Рассинхрон проявится как
 * молча подставленный тьютор по умолчанию.
 *
 * Картинки есть у Ryan, Mia, James и Lily (id nate, luna, clive, selene) — нарезаны из листов
 * `references/mascot/` скриптом `scripts/avatar/split-tutor-sheets.mjs`.
 * У остальных пока эмодзи-заглушка. Поля лежат в данных, а не в компоненте,
 * чтобы новая картинка была правкой одного списка.
 */
export interface Tutor extends Option {
  /** Эмодзи-заглушка — для тьюторов без картинок. */
  readonly avatar: string;
  /**
   * Портрет (квадрат, голова и плечи) — кружок в недавних диалогах и истории;
   * превью (3:4, в рост) — карточка в выборе тьютора.
   */
  readonly images?: { readonly portrait: string; readonly preview: string };
  /** Фон карточки под заглушкой; с настоящей картинкой останется как подложка. */
  readonly backdrop: string;
  /** Акцент собеседника — используется вызывающей стороной для перевода подписи (см. `TutorCarousel.i18n.ts`). */
  readonly accentKey: 'american' | 'british' | 'german' | 'brazilian';
  /**
   * Не предлагать в выборе собеседника. Из каталога не убираем: её прошлые
   * диалоги в истории и на главной должны находить имя и аватарку.
   */
  readonly hidden?: boolean;
  /**
   * На каком языке тьютор говорит — тот же `language`, что у персоны в
   * `catalog.ts`. Выбрать собеседника можно только из тьюторов языка
   * разговора: у каждого свой голос Inworld.
   */
  readonly language: 'en' | 'de' | 'pt';
}

export const TUTORS = [
  // id у английских — прежние имена (см. `catalog.ts`): на них завязана база.
  // Порядок чередует пол и акцент: Ryan, Lily, James, Mia (2026-10-08).
  {
    id: 'nate',
    label: 'Ryan',
    language: 'en',
    accentKey: 'american',
    avatar: '👨',
    images: { portrait: '/tutors/nate-portrait.webp', preview: '/tutors/nate-preview.webp' },
    backdrop: 'linear-gradient(160deg, #d98a4a, #2e1f14)',
  },
  {
    id: 'selene',
    label: 'Lily',
    language: 'en',
    accentKey: 'british',
    avatar: '👩‍🦰',
    images: { portrait: '/tutors/selene-portrait.webp', preview: '/tutors/selene-preview.webp' },
    backdrop: 'linear-gradient(160deg, #ff9d6c, #4a1942)',
  },
  {
    id: 'clive',
    label: 'James',
    language: 'en',
    accentKey: 'british',
    avatar: '👨‍🦱',
    images: { portrait: '/tutors/clive-portrait.webp', preview: '/tutors/clive-preview.webp' },
    backdrop: 'linear-gradient(160deg, #4f7ea8, #16232e)',
  },
  {
    id: 'luna',
    label: 'Mia',
    language: 'en',
    accentKey: 'american',
    avatar: '👩‍🦳',
    images: { portrait: '/tutors/luna-portrait.webp', preview: '/tutors/luna-preview.webp' },
    backdrop: 'linear-gradient(160deg, #8f7fe8, #1f1a3d)',
  },
  {
    id: 'hana',
    label: 'Hana',
    language: 'en',
    accentKey: 'american',
    avatar: '👧',
    backdrop: 'linear-gradient(160deg, #5f8fe8, #1a2448)',
    // Скрыта из выбора, пока делается её персонаж (2026-10-08).
    hidden: true,
  },
  {
    id: 'annika',
    label: 'Annika',
    language: 'de',
    accentKey: 'german',
    avatar: '👩‍🦰',
    backdrop: 'linear-gradient(160deg, #6aa7d9, #1b2a3a)',
  },
  {
    id: 'kilian',
    label: 'Kilian',
    language: 'de',
    accentKey: 'german',
    avatar: '🧑‍🦱',
    backdrop: 'linear-gradient(160deg, #7c9a5e, #1f2a18)',
  },
  {
    id: 'carina',
    label: 'Carina',
    language: 'de',
    accentKey: 'german',
    avatar: '👩',
    backdrop: 'linear-gradient(160deg, #e07a8c, #3a1a24)',
  },
  {
    id: 'bruna',
    label: 'Bruna',
    language: 'pt',
    accentKey: 'brazilian',
    avatar: '👩🏽',
    backdrop: 'linear-gradient(160deg, #3fb58f, #13322a)',
  },
  {
    id: 'tatiana',
    label: 'Tatiana',
    language: 'pt',
    accentKey: 'brazilian',
    avatar: '👩🏾‍🦱',
    backdrop: 'linear-gradient(160deg, #f2b33d, #3d2a0e)',
  },
  {
    id: 'murilo',
    label: 'Murilo',
    language: 'pt',
    accentKey: 'brazilian',
    avatar: '🧔🏽',
    backdrop: 'linear-gradient(160deg, #b0784a, #2e1d10)',
  },
] as const satisfies readonly Tutor[];

/** Идентификатор выбранного собеседника. */
export type TutorId = (typeof TUTORS)[number]['id'];

/**
 * Тьютор в адресе страницы — его имя, как на экране (`?tutor=ryan`), а не
 * внутренний id (`nate`): id английских тьюторов — их прежние имена, и
 * адрес выдавал бы кухню. Имена в каталоге не повторяются.
 */
export function tutorSlug(id: string): string {
  return TUTORS.find((tutor) => tutor.id === id)?.label.toLowerCase() ?? id;
}

/**
 * Обратно: имя из адреса → id. Старый вид `?tutorId=nate` (ссылки, открытые
 * до переименования) тоже понимаем — по id.
 */
export function tutorFromSlug(value: string | null | undefined): TutorId | undefined {
  if (!value) return undefined;
  const slug = value.toLowerCase();
  return TUTORS.find((tutor) => tutor.label.toLowerCase() === slug || tutor.id === slug)?.id;
}

/**
 * Тьюторы одного языка, из которых можно выбрать, — в том же порядке, что в
 * каталоге; первый — тьютор языка по умолчанию. Скрытые сюда не попадают.
 */
export function tutorsFor(language: Tutor['language']): (typeof TUTORS)[number][] {
  return TUTORS.filter((tutor) => tutor.language === language && !('hidden' in tutor && tutor.hidden));
}
